import React, { useContext, useEffect, useRef, useState } from 'react';
import { Context as AuthContext } from '../../../contexts/authContext';
import { MDBox } from '../../MDBox';
import { getSignInProviders } from '../../../services/authService';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// Google's global is also the Maps namespace (typed elsewhere); sign-in uses its accounts part.
const gis = () => (window as any).google;

const loadGoogle = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (gis()?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`) as HTMLScriptElement | null;
    const s = existing || document.createElement('script');
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('Google sign-in could not load.')));
    if (!existing) {
      s.src = GIS_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
  });

/**
 * "Sign in with Google", for organizations on Google Workspace. Shown only when the claims API
 * has Google sign-in on (it gives the public client id); Google's own button, whose sign-in the
 * claims API checks exactly as it checks Microsoft's.
 */
export default function GoogleSignInButton() {
  const { signInWithGoogle } = useContext(AuthContext);
  const box = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getSignInProviders()
      .then((p) => live && setClientId(p?.google?.clientId || null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId || !box.current) return;
    loadGoogle()
      .then(() => {
        gis().accounts.id.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          auto_select: false,
          callback: (r: { credential?: string }) => {
            if (r?.credential && signInWithGoogle) signInWithGoogle(r.credential);
          },
        });
        gis().accounts.id.renderButton(box.current, { theme: 'outline', size: 'large', text: 'signin_with', width: 320, locale: 'en' });
      })
      .catch(() => undefined);
  }, [clientId, signInWithGoogle]);

  if (!clientId) return null;
  return (
    <MDBox mt={1} display="flex" justifyContent="center">
      <div ref={box} />
    </MDBox>
  );
}
